import type { GameDto } from '@brig/api-types';
import { button, paper } from '@brig/theme/styles';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { ApiRequestError } from '../api/client.ts';
import { useApi, useGame, useLive } from '../api/hooks.ts';
import { LoadingNotice } from '../components/LoadingNotice.tsx';
import { HintMemoryContext, useProfileHintMemory } from '../learn/hintMemory.ts';
import { AwaitingOpponent } from './AwaitingOpponent.tsx';
import { GameScreen } from './screen/GameScreen.tsx';
import { serverSource } from './server.ts';

/**
 * A game the server keeps, on the game screen. Another game (new, or loaded from a log) opens
 * through `onSwitch`; the screen starts afresh for it.
 */
export function ServerGame({
  id,
  onSwitch,
  onMissing,
  title,
}: {
  id: string;
  onSwitch: (game: GameDto) => void;
  /** The game is not (or no longer) there; without it, the screen says so. */
  onMissing?: () => void;
  title?: string;
}) {
  const { t } = useTranslation();
  const game = useGame(id);
  const missing = game.error instanceof ApiRequestError && game.error.status === 404;
  const { refetch } = game;
  const reload = useCallback(() => void refetch(), [refetch]);
  useEffect(() => {
    if (missing) onMissing?.();
  }, [missing, onMissing]);
  if (game.isPending || (missing && onMissing)) return <LoadingNotice />;
  if (game.isError) {
    return (
      <section className={paper} role="alert">
        <p>{t(missing ? 'game.load.notFound' : 'game.load.failed')}</p>
        {missing ? null : (
          <button type="button" className={button({ tone: 'label' })} onClick={() => void game.refetch()}>
            {t('game.load.retry')}
          </button>
        )}
        <Link to="/logbook">{t('game.load.back')}</Link>
      </section>
    );
  }
  if (game.data.status === 'pending') return <AwaitingOpponent game={game.data} onJoined={reload} />;
  return <Screen key={game.data.id} game={game.data} onSwitch={onSwitch} {...(title ? { title } : {})} />;
}

function Screen({
  game,
  onSwitch,
  title,
}: {
  game: GameDto;
  onSwitch: (game: GameDto) => void;
  title?: string;
}) {
  const api = useApi();
  const live = useLive();
  // The screen reads its source once: the game as loaded, then the server's answers.
  const [source] = useState(() => serverSource(api, live, game, onSwitch));
  // First-time hints follow the captain, not the side (U-LEARN-08/09).
  const hints = useProfileHintMemory();
  return (
    <HintMemoryContext value={hints}>
      <GameScreen source={source} {...(title ? { title } : {})} />
    </HintMemoryContext>
  );
}
